import {
    AuditLog,
    AuditLogStreamDestination,
    Bookmark,
    ConfirmDeletionInput,
    DeletionVerificationRequestResult,
    Investment,
    Member,
    MemberPermissionType,
    Memo,
    Pipeline,
    Team,
    Whitelabel,
    Workspace,
    WorkspaceAuthorizedAuthenticationMethods,
    WorkspaceCustomSubdomains,
    WorkspaceDataRetentionPolicy,
    WorkspaceDataRetentionPolicyInput,
    WorkspaceDomainConnectSetupIntent,
    WorkspaceIdentity,
    WorkspaceIdentityForceLogoutResponse,
    WorkspaceIdentityPortalLink,
    WorkspaceIdentityPortalLinkIntent,
    WorkspaceNetworkAccess,
    WorkspaceNetworkAccessRule,
    WorkspaceVerifiedDomain,
} from '@/types';
import { SessionLoginMethod } from '@/types/database/generated/Session';
import { WorkspaceTemplateKey } from '@/types/workspaceTemplates';

import { apiClient } from '../http';
import { toWorkspaceMutationInput } from './resourceMutationPayloads';
import { createSanitizedResourceService } from './resources';

export type WorkspaceResourceCollection =
    | 'dashboards'
    | 'folders'
    | 'investments'
    | 'lists'
    | 'members'
    | 'memos'
    | 'pipelines'
    | 'teams';

export type WorkspaceDomainInput = {
    domain?: string;
    favicon?: string;
    hostname?: string;
    name?: string;
    origin?: string;
    status?: string;
    title?: string;
    url?: string;
    visibility?: string;
};

export type WorkspaceVerifiedDomainInput = {
    domain?: string;
    url?: string;
};

export type AuditLogStreamDestinationInput = {
    config?: Record<string, unknown>;
    enabled?: boolean;
    name: string;
};

export type AuditLogExportFormat = 'csv' | 'json' | 'xlsx' | 'zip';
export type AuditLogExportFilterField =
    | 'action'
    | 'actor'
    | 'browser'
    | 'createdAt'
    | 'deviceType'
    | 'entityId'
    | 'ipAddress'
    | 'permission'
    | 'resource';
export type AuditLogExportFilter = {
    field: AuditLogExportFilterField;
    operator: 'contains' | 'equals' | 'greaterThan' | 'lessThan' | 'notEquals';
    value: boolean | number | string | Array<boolean | number | string>;
    valueJoin?: 'and' | 'or';
};
export type AuditLogExportInput = {
    advancedFilter?: import('@/shared/components/filters').AdvancedFilterDefinition<AuditLogExportFilter>;
    filters: AuditLogExportFilter[];
    search: string;
};
export type AuditLogQueryMode = 'all' | 'actionTaken';
export type AuditLogQueryInput = AuditLogExportInput & {
    cursor?: string;
    limit?: number;
    mode?: AuditLogQueryMode;
};
export type AuditLogQueryFacets = {
    actions: string[];
    actors: string[];
    browsers: string[];
    deviceTypes: string[];
    permissions: string[];
    resources: string[];
};
export type AuditLogQueryPage = {
    actionTakenTotal?: number;
    facets?: AuditLogQueryFacets;
    hasMore: boolean;
    items: AuditLog[];
    nextCursor?: string;
    total?: number;
};

export type WorkspaceNetworkAccessInput = {
    enabled: boolean;
    rules: Array<
        Pick<WorkspaceNetworkAccessRule, 'label' | 'value'> &
            Partial<Pick<WorkspaceNetworkAccessRule, 'description' | 'id'>>
    >;
};

export type WorkspaceAuthorizedAuthenticationMethodsInput = {
    enabled: boolean;
    methods: SessionLoginMethod[];
};

export type WorkspaceCustomSubdomainsInput = {
    enabled: boolean;
};

export type WorkspaceCustomSubdomainInput = {
    hostname: string;
};

export type WorkspaceIdentityInput = {
    samlEnabled?: boolean;
    scimEnabled?: boolean;
    sessionDurationDays?: number;
};

export type WorkspaceIdentityPortalLinkInput = {
    intent: WorkspaceIdentityPortalLinkIntent;
};

export type WorkspaceMemberInvitationInput = {
    emailAddress: string;
    permission: MemberPermissionType;
};

export interface WorkspaceCreateInput {
    avatar?: string;
    emoji?: string;
    name?: string;
    subdomain?: string;
    templateKey?: WorkspaceTemplateKey;
}

const base = createSanitizedResourceService<
    Workspace,
    ReturnType<typeof toWorkspaceMutationInput>,
    ReturnType<typeof toWorkspaceMutationInput>
>('/api/workspaces', toWorkspaceMutationInput, toWorkspaceMutationInput);

export const workspaceService = {
    ...base,
    create: (input: WorkspaceCreateInput) => apiClient.post<Workspace, WorkspaceCreateInput>('/api/workspaces', input),
    createDomain: (workspaceId: string, input: WorkspaceDomainInput) =>
        apiClient.post<Bookmark, WorkspaceDomainInput>(`/api/workspaces/${workspaceId}/domains`, input),
    createVerifiedDomain: (workspaceId: string, input: WorkspaceVerifiedDomainInput) =>
        apiClient.post<WorkspaceVerifiedDomain, WorkspaceVerifiedDomainInput>(
            `/api/workspaces/${workspaceId}/domains`,
            input,
        ),
    deleteWithVerification: (workspaceId: string, input: ConfirmDeletionInput) =>
        apiClient.delete<void>(`/api/workspaces/${workspaceId}`, { data: input }),
    deleteDomain: (workspaceId: string, domainId: string) =>
        apiClient.delete<void>(`/api/workspaces/${workspaceId}/domains/${domainId}`),
    deleteVerifiedDomain: (workspaceId: string, domainId: string) =>
        apiClient.delete<WorkspaceVerifiedDomain>(`/api/workspaces/${workspaceId}/domains/${domainId}`),
    exportAuditLogs: (workspaceId: string, format: AuditLogExportFormat, input: AuditLogExportInput) =>
        apiClient.response.post<Blob, AuditLogExportInput>(
            `/api/workspaces/${workspaceId}/logs/export/${format}`,
            input,
            { responseType: 'blob' },
        ),
    deleteAuditLogStreamDestination: (workspaceId: string, provider: string) =>
        apiClient.delete<void>(`/api/workspaces/${workspaceId}/log-streams/${provider}`),
    getActive: () => apiClient.get<Workspace>('/api/active/workspace'),
    getAuditLogs: (workspaceId: string) => apiClient.get<AuditLog[]>(`/api/workspaces/${workspaceId}/logs`),
    queryAuditLogs: (workspaceId: string, input: AuditLogQueryInput) =>
        apiClient.post<AuditLogQueryPage, AuditLogQueryInput>(`/api/workspaces/${workspaceId}/logs/query`, input),
    getAuditLogStreamDestinations: (workspaceId: string) =>
        apiClient.get<AuditLogStreamDestination[]>(`/api/workspaces/${workspaceId}/log-streams`),
    getDomains: (workspaceId: string) => apiClient.get<Bookmark[]>(`/api/workspaces/${workspaceId}/domains`),
    getVerifiedDomains: (workspaceId: string) =>
        apiClient.get<WorkspaceVerifiedDomain[]>(`/api/workspaces/${workspaceId}/domains`),
    getAuthorizedAuthenticationMethods: (workspaceId: string) =>
        apiClient.get<WorkspaceAuthorizedAuthenticationMethods>(
            `/api/workspaces/${workspaceId}/authorized-authentication-methods`,
        ),
    getCustomSubdomains: (workspaceId: string) =>
        apiClient.get<WorkspaceCustomSubdomains>(`/api/workspaces/${workspaceId}/custom-subdomains`),
    getDataRetentionPolicy: (workspaceId: string) =>
        apiClient.get<WorkspaceDataRetentionPolicy>(`/api/workspaces/${workspaceId}/data-retention`),
    getFavorites: (workspaceId: string) => apiClient.get<unknown[]>(`/api/workspaces/${workspaceId}/favorites`),
    getFolders: (workspaceId: string) => apiClient.get<unknown[]>(`/api/workspaces/${workspaceId}/folders`),
    getDashboards: (workspaceId: string) => apiClient.get<unknown[]>(`/api/workspaces/${workspaceId}/dashboards`),
    getInvestments: (workspaceId: string) => apiClient.get<Investment[]>(`/api/workspaces/${workspaceId}/investments`),
    getLists: (workspaceId: string) => apiClient.get<unknown[]>(`/api/workspaces/${workspaceId}/lists`),
    getMembers: (workspaceId: string) => apiClient.get<Member[]>(`/api/workspaces/${workspaceId}/members`),
    getMemos: (workspaceId: string) => apiClient.get<Memo[]>(`/api/workspaces/${workspaceId}/memos`),
    getNetworkAccess: (workspaceId: string) =>
        apiClient.get<WorkspaceNetworkAccess>(`/api/workspaces/${workspaceId}/network-access`),
    getIdentity: (workspaceId: string) => apiClient.get<WorkspaceIdentity>(`/api/workspaces/${workspaceId}/identity`),
    getPipelines: (workspaceId: string) => apiClient.get<Pipeline[]>(`/api/workspaces/${workspaceId}/pipelines`),
    requestDeletionVerification: (workspaceId: string) =>
        apiClient.post<DeletionVerificationRequestResult>(`/api/workspaces/${workspaceId}/deletion-verification`),
    getPrivateDomains: (workspaceId: string) =>
        apiClient.get<Bookmark[]>(`/api/workspaces/${workspaceId}/domains/private`),
    getPrivateVerifiedDomains: (workspaceId: string) =>
        apiClient.get<WorkspaceVerifiedDomain[]>(`/api/workspaces/${workspaceId}/domains/private`),
    getResourceCollection: <TResource = unknown>(workspaceId: string, resource: WorkspaceResourceCollection) =>
        apiClient.get<TResource[]>(`/api/workspaces/${workspaceId}/${resource}`),
    getTeams: (workspaceId: string) => apiClient.get<Team[]>(`/api/workspaces/${workspaceId}/teams`),
    getWhitelabel: (workspaceId: string) => apiClient.get<Whitelabel>(`/api/workspaces/${workspaceId}/whitelabel`),
    inviteMember: (workspaceId: string, input: WorkspaceMemberInvitationInput) =>
        apiClient.post<Member, WorkspaceMemberInvitationInput>(`/api/workspaces/${workspaceId}/members`, input),
    listActive: <TParams extends Record<string, unknown> = Record<string, unknown>>(params?: TParams) =>
        apiClient.get<Workspace[], TParams>('/api/workspaces/active', { params }),
    listPending: <TParams extends Record<string, unknown> = Record<string, unknown>>(params?: TParams) =>
        apiClient.get<Workspace[], TParams>('/api/workspaces/pending', { params }),
    setBrandingRemoval: (workspaceId: string, removeBranding: boolean) =>
        apiClient.put<Workspace, { removeBranding: boolean }>(`/api/workspaces/${workspaceId}/branding`, {
            removeBranding,
        }),
    setAuditLogStreamDestination: (workspaceId: string, provider: string, input: AuditLogStreamDestinationInput) =>
        apiClient.put<AuditLogStreamDestination, AuditLogStreamDestinationInput>(
            `/api/workspaces/${workspaceId}/log-streams/${provider}`,
            input,
        ),
    setSamlSso: (workspaceId: string, samlEnabled: boolean) =>
        apiClient.post<Workspace, { samlEnabled: boolean }>(`/api/workspaces/${workspaceId}/saml`, { samlEnabled }),
    prepareCustomSubdomainDomainConnect: (workspaceId: string, customSubdomainId: string) =>
        apiClient.post<WorkspaceDomainConnectSetupIntent, Record<string, never>>(
            `/api/workspaces/${workspaceId}/custom-subdomains/${customSubdomainId}/domain-connect`,
            {},
        ),
    prepareVerifiedDomainDomainConnect: (workspaceId: string, domainId: string) =>
        apiClient.post<WorkspaceDomainConnectSetupIntent, Record<string, never>>(
            `/api/workspaces/${workspaceId}/domains/${domainId}/domain-connect`,
            {},
        ),
    verifyDomain: (workspaceId: string, domainId: string) =>
        apiClient.post<WorkspaceVerifiedDomain, Record<string, never>>(
            `/api/workspaces/${workspaceId}/domains/${domainId}/verify`,
            {},
        ),
    createIdentityPortalLink: (workspaceId: string, input: WorkspaceIdentityPortalLinkInput) =>
        apiClient.post<WorkspaceIdentityPortalLink, WorkspaceIdentityPortalLinkInput>(
            `/api/workspaces/${workspaceId}/identity/admin-portal-link`,
            input,
        ),
    forceLogoutMembers: (workspaceId: string) =>
        apiClient.post<WorkspaceIdentityForceLogoutResponse, Record<string, never>>(
            `/api/workspaces/${workspaceId}/identity/force-logout`,
            {},
        ),
    updateIdentity: (workspaceId: string, input: WorkspaceIdentityInput) =>
        apiClient.put<WorkspaceIdentity, WorkspaceIdentityInput>(`/api/workspaces/${workspaceId}/identity`, input),
    updateNetworkAccess: (workspaceId: string, input: WorkspaceNetworkAccessInput) =>
        apiClient.put<WorkspaceNetworkAccess, WorkspaceNetworkAccessInput>(
            `/api/workspaces/${workspaceId}/network-access`,
            input,
        ),
    updateCustomSubdomains: (workspaceId: string, input: WorkspaceCustomSubdomainsInput) =>
        apiClient.put<WorkspaceCustomSubdomains, WorkspaceCustomSubdomainsInput>(
            `/api/workspaces/${workspaceId}/custom-subdomains`,
            input,
        ),
    updateDataRetentionPolicy: (workspaceId: string, input: WorkspaceDataRetentionPolicyInput) =>
        apiClient.put<WorkspaceDataRetentionPolicy, WorkspaceDataRetentionPolicyInput>(
            `/api/workspaces/${workspaceId}/data-retention`,
            input,
        ),
    createCustomSubdomain: (workspaceId: string, input: WorkspaceCustomSubdomainInput) =>
        apiClient.post<WorkspaceCustomSubdomains, WorkspaceCustomSubdomainInput>(
            `/api/workspaces/${workspaceId}/custom-subdomains`,
            input,
        ),
    verifyCustomSubdomain: (workspaceId: string, customSubdomainId: string) =>
        apiClient.post<WorkspaceCustomSubdomains, Record<string, never>>(
            `/api/workspaces/${workspaceId}/custom-subdomains/${customSubdomainId}/verify`,
            {},
        ),
    deleteCustomSubdomain: (workspaceId: string, customSubdomainId: string) =>
        apiClient.delete<WorkspaceCustomSubdomains>(
            `/api/workspaces/${workspaceId}/custom-subdomains/${customSubdomainId}`,
        ),
    updateAuthorizedAuthenticationMethods: (
        workspaceId: string,
        input: WorkspaceAuthorizedAuthenticationMethodsInput,
    ) =>
        apiClient.put<WorkspaceAuthorizedAuthenticationMethods, WorkspaceAuthorizedAuthenticationMethodsInput>(
            `/api/workspaces/${workspaceId}/authorized-authentication-methods`,
            input,
        ),
    updateWhitelabel: (workspaceId: string, input: Partial<Whitelabel>) =>
        apiClient.put<Whitelabel, Partial<Whitelabel>>(`/api/workspaces/${workspaceId}/whitelabel`, input),
};
